import type { FC } from 'react';
import { Avatar, Box, chakra, Spacer, Stack, VStack } from '@chakra-ui/react';

import Button from 'components/_core/Button';
import Heading from 'components/_core/Heading';
import SunshineCard from 'components/_core/SunshineCard';
import SquircleIcon from 'components/_core/SunshineSquircleIcon';
import StepperProgressBar from 'components/Stepper/StepperProgressBar';
import PayPerSeatMarketingCallout from 'features/Team/components/PayPerSeatMarketingCallout';
import ListHeading from 'features/Team/pages/Members/components/ListHeading';
import { QuickInvitations } from 'features/Team/pages/MembersInvite/hooks/useQuickInvitations';
import * as Layout from 'layouts/StickyStepperLayout';

import { useMemberInviteState } from '../../../../MemberInviteProvider';
import locales from './locales';

interface LegalBeneficiarySelectorFormProps {
  handleCancel: () => void;
  legalBeneficiaries: QuickInvitations;
  onSubmit: () => void;
}

const LegalBeneficiarySelectorForm: FC<LegalBeneficiarySelectorFormProps> = ({
  handleCancel,
  legalBeneficiaries,
  onSubmit,
}) => {
  const { dispatch, selectedLegalBeneficiary } = useMemberInviteState();
  const setSelectedBeneficiary = (userId: string) => {
    const selectedBeneficiary = legalBeneficiaries.find(
      (invitation) => invitation.companyUserId === userId,
    );
    dispatch({
      payload: selectedBeneficiary ? selectedBeneficiary : false,
      type: 'SELECT_LEGAL_BENEFICIARY',
    });
  };

  const resetSelectedBeneficiary = () => setSelectedBeneficiary('');

  const handleSubmit = () => {
    onSubmit();
  };

  return (
    <chakra.form display="contents" onSubmit={handleSubmit}>
      <Layout.ContentsContainer gap="space-24">
        <VStack align="flex-start" spacing="space-16">
          <Heading size="lg">{locales.title}</Heading>
          <PayPerSeatMarketingCallout />
        </VStack>
        <Box paddingTop="space-16">
          <SunshineCard.RadioGroup
            onClick={resetSelectedBeneficiary}
            value={selectedLegalBeneficiary === false ? 'undefined' : ''}
          >
            <SunshineCard
              alignItems="center"
              title={locales.newInvitation}
              value="undefined"
              variant="radio"
            >
              <SunshineCard.Slot name="aside">
                <SquircleIcon name="home" />
              </SunshineCard.Slot>
            </SunshineCard>
          </SunshineCard.RadioGroup>
        </Box>

        <Box paddingTop="space-32">
          <ListHeading
            description={locales.legalBeneficiaries.subtitle}
            title={locales.legalBeneficiaries.title}
          />
          <Stack width="full">
            <SunshineCard.RadioGroup
              onChange={setSelectedBeneficiary}
              value={
                selectedLegalBeneficiary
                  ? selectedLegalBeneficiary.companyUserId
                  : ''
              }
            >
              {legalBeneficiaries.map((legalBeneficiary) => (
                <SunshineCard
                  alignItems="center"
                  key={legalBeneficiary.companyUserId}
                  title={`${legalBeneficiary.firstName} ${legalBeneficiary.lastName}`}
                  value={legalBeneficiary.companyUserId}
                  variant="radio"
                >
                  <SunshineCard.Slot name="aside">
                    <Avatar
                      background="grey.100"
                      borderRadius="radius-10"
                      boxSize="space-32"
                      color="text-primary"
                      fontWeight="weight-500"
                      name={`${legalBeneficiary.firstName} ${legalBeneficiary.lastName}`}
                    />
                  </SunshineCard.Slot>
                  <SunshineCard.Content>
                    <SunshineCard.Text>
                      {legalBeneficiary.email}
                    </SunshineCard.Text>
                  </SunshineCard.Content>
                </SunshineCard>
              ))}
            </SunshineCard.RadioGroup>
          </Stack>
        </Box>
      </Layout.ContentsContainer>
      <Layout.FooterContainer>
        <StepperProgressBar />

        <Layout.FooterButtonsContainer>
          <Button onClick={handleCancel} variant="inline-secondary">
            {locales.cancel}
          </Button>
          <Spacer />
          <Button type="submit">{locales.submit}</Button>
        </Layout.FooterButtonsContainer>
      </Layout.FooterContainer>
    </chakra.form>
  );
};

export default LegalBeneficiarySelectorForm;
